import { FC, useEffect, useMemo, useState } from 'react';
import {
  bulkUpsertUsersToGroupLink,
  getGroupList,
  getUserList,
} from 'api/access';
import { errorMessages, successMessages } from 'constants/errors';
import { GROUP_ROLE_OPTIONS } from 'constants/profile';
import { useAppSelector } from 'hooks';
import { ReactComponent as AddedUsers } from 'images/newIcons/personGroupFill.svg';
import { GroupInfo, GroupMember, PublicUserInfo } from 'interfaces';
import { accountSelector } from 'store/slices/auth/selectors';

import { Button, Modal, MultiSelect, Select, TextInput } from 'components/ui';
import { notify } from 'utils';
import { handleNotify } from 'utils/notifications';

export interface GroupMemberDetailsModalProps {
  group: GroupInfo;
  member?: GroupMember;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormState {
  userIds: number[];
  groupId: number;
  position: string;
  role: string;
}

const GroupMemberDetailsModal: FC<GroupMemberDetailsModalProps> = ({
  group,
  onClose,
  member,
  onSuccess,
}) => {
  const currentUser = useAppSelector(accountSelector);

  const [formState, setFormState] = useState<FormState>({
    userIds: member ? [member.id] : [],
    groupId: group.id || 0,
    position: member?.position || '',
    role: member?.role || 'user',
  });

  const { userIds, groupId, position, role } = formState;

  const [users, setUsers] = useState<PublicUserInfo[]>([]);
  const [groups, setGroups] = useState<GroupInfo[]>([]);

  const updateFormState = <K extends keyof FormState>(
    key: K,
    value: FormState[K]
  ) => {
    setFormState((prevState) => ({ ...prevState, [key]: value }));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [users, groups] = await Promise.all([
          getUserList(),
          getGroupList(),
        ]);

        setUsers(users);
        setGroups(groups);
      } catch (error) {
        notify.error(errorMessages.GET_GROUP_LIST_ERROR);
      }
    };

    fetchData();
  }, []);

  const getGroupMembers = (groupId: number) =>
    groups.find((group) => group.id === groupId)?.members ?? [];

  const getAvailableUsers = (groupMembers: GroupMember[]) => {
    const groupMemberIds = new Set(groupMembers.map((member) => member.id));

    return users.filter((user) => !groupMemberIds.has(user.id));
  };

  const groupMembers = getGroupMembers(groupId);
  const availableUsers = getAvailableUsers(groupMembers);

  const userOptions = availableUsers.flatMap((user) =>
    user.id !== currentUser?.id
      ? { label: user.verboseName, value: user.id }
      : []
  );

  const selectedUsers = useMemo(
    () =>
      userIds.flatMap((id) => {
        const label = userOptions.find((option) => option.value === id)?.label;

        return label ? [label] : [];
      }),
    [userIds, userOptions]
  );

  const handleSelect = (selectedUsers: string[]) => {
    const selectedIds = selectedUsers.flatMap((label) => {
      const value = userOptions.find((option) => option.label === label)?.value;

      return value !== undefined ? [value] : [];
    });

    updateFormState('userIds', selectedIds);
  };

  const handleSave = async () => {
    try {
      const isSingleUser = userIds.length === 1;

      const successMessage = isSingleUser
        ? successMessages.USER_TO_GROUP_LINK_SUCCESS
        : successMessages.USERS_TO_GROUP_LINK_SUCCESS;

      const errorMessage = isSingleUser
        ? errorMessages.USER_TO_GROUP_LINK_ERROR
        : errorMessages.USERS_TO_GROUP_LINK_ERROR;

      await handleNotify(
        bulkUpsertUsersToGroupLink(
          userIds.map((userId) => ({
            userId: userId.toString(),
            groupId: groupId.toString(),
            position,
            role,
          }))
        ),
        successMessage,
        errorMessage
      );

      onSuccess();
      onClose();
    } catch (error) {
      console.error(error);
    }
  };

  const getModalDetails = () => {
    if (member?.username) {
      return `Изменить роль ${member.username}`;
    }
    if (group?.title) {
      return `Добавить пользователей в группу: ${group.title}`;
    }
    return null;
  };

  const isSaveBtnDisabled =
    formState.userIds.length === 0 ||
    formState.groupId === 0 ||
    formState.role === '' ||
    formState.position === '';

  return (
    <Modal width={460} keyboard isBlurred onClose={onClose}>
      <div className="w-full bg-dark border border-solid border-tpg_light rounded-[10px] p-12">
        <div className="tpg-h4 flex justify-center text-center">
          {getModalDetails()}
        </div>
        <div className="flex flex-col gap-3 py-6">
          {!member && (
            <MultiSelect
              value={selectedUsers}
              onSelect={handleSelect}
              options={userOptions}
              theme="light"
              placeholder="Позывной"
              searchPlaceholder="Введите позывной"
              endIcon={<AddedUsers className="icon-container multi-icon" />}
              selectedCount={userIds.length}
            />
          )}
          <Select
            value={role}
            withEmpty={false}
            onSelect={(v) => updateFormState('role', v)}
            options={GROUP_ROLE_OPTIONS}
            theme="light"
            placeholder="Роль"
            unknownOptionLabel="Роль"
          />
          <TextInput
            value={position}
            onChange={(v) => updateFormState('position', v)}
            placeholder="Должность"
          />
        </div>
        <Button
          onClick={handleSave}
          title={'Добавить'}
          className="w-full"
          disabled={isSaveBtnDisabled}
        />
      </div>
    </Modal>
  );
};

export default GroupMemberDetailsModal;
